import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useReducer, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import feedbackService from 'services/feedback';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
} from 'utils/constants';
import { getAIPreview, getToneDescription } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { currentAssignmentId, currentAssignment, feedback, setFeedback } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();

    const [state, dispatch] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'SET_SELECTED_FEEDBACK_ID':
                    return {
                        selectedFeedbackId: action.payload,
                    };
                case 'SET_SELECTED_FEEDBACK_ID_TO_FIRST':
                    const target = feedback.find(({ id }) => id === params.feedbackId);
                    if (!target?.feedback?.improvement_suggestions?.filter(({ active }) => active).length) return state;
                    return typeof target.feedback === 'string'
                        ? state
                        : {
                              selectedFeedbackId: target.feedback.improvement_suggestions.filter(
                                  ({ active }) => active
                              )[0].id,
                          };
                default:
                    return state;
            }
        },
        {
            selectedFeedbackId: null,
        }
    );

    const { selectedFeedbackId } = state;
    const setSelectedFeedbackId = (id) => dispatch({ type: 'SET_SELECTED_FEEDBACK_ID', payload: id });

    const [introductionDialog, setIntroductionDialog] = useState({ open: false });
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const currentFeedback = feedback?.find(({ id }) => id === params.feedbackId);
    const currentFeedbackIndex = feedback?.findIndex(({ id }) => id === currentFeedback.id);

    const getHighlights = () => {
        const highlights =
            currentFeedback?.feedback?.feedback_type === 'rubric'
                ? currentFeedback?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                      ?.negative?.locations || []
                : currentFeedback?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                      ?.locations || [];
        return highlights.map((item) => ({ id: selectedFeedbackId, value: item }));
    };

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const onClickInfo = () => {
        setIntroductionDialog({ open: true });
    };

    const onClickPrevAttempt = () => {
        const currentFeedbackIndex = feedback.findIndex(({ id }) => id === params.feedbackId);
        const targetFeedback = feedback[currentFeedbackIndex - 1];
        history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
    };

    const onClickNextAttempt = () => {
        const currentFeedbackIndex = feedback.findIndex(({ id }) => id === params.feedbackId);
        if (currentFeedbackIndex === feedback.length - 1) {
            history.push(`/assignments/${currentAssignmentId}/feedback`);
        } else {
            const targetFeedback = feedback[currentFeedbackIndex + 1];
            history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
        }
    };

    const onClickRegenerate = () => {
        setDialog({
            open: true,
            message: 'Regenerate Feedback?',
            description: 'This will permanently delete all existing feedback for this draft, and cannot be undone.',
            note: '',
            variant: 'primary',
            actionButtonText: 'Regenerate',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                closeDialog();
                setIsGenerating(true);
                try {
                    const response = await getAIPreview({
                        owner_account_id: currentAssignment.owner_account_id,
                        target: 'feedback',
                        assignment_id: currentAssignment.id,
                        description: currentAssignment.description,
                        rubric: currentAssignment.rubric,
                        baselineAssignment: currentAssignment.baseline?.assignment,
                        baselineCriteria: currentAssignment.baseline?.criteria,
                        content_bank_resources: currentAssignment.content_bank_resources,
                        structure: currentAssignment.ai_feedback_settings.structure || DEFAULT_FEEDBACK_STRUCTURE,
                        numSuggestions:
                            currentAssignment.ai_feedback_settings.numSuggestions || DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                        suggestionLength:
                            currentAssignment.ai_feedback_settings.suggestionLength ||
                            DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                        languageLevel:
                            currentAssignment.ai_feedback_settings.languageLevel || DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                        tone: currentAssignment.ai_feedback_settings.tone || DEFAULT_FEEDBACK_TONE,
                        toneDescription: getToneDescription(currentAssignment.ai_feedback_settings.tone),
                        customInstructions: currentAssignment.ai_feedback_settings.customInstructions || '',
                        feedbackCustomContext: currentAssignment.ai_feedback_context,
                        gradingCustomContext: currentAssignment.ai_grading_context,
                        submission: currentFeedback.submission,
                    });
                    const result = await response.json();
                    const updatedFeedback = {
                        ...currentFeedback,
                        modified_on: new Date().getTime(),
                        feedback: result.data.feedback,
                        usage: result.usage,
                        is_failed: result.fail || result.is_completely_fail,
                    };
                    setFeedback((current) => {
                        return current.map((feedback) => {
                            if (feedback.id !== updatedFeedback.id) return feedback;
                            feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                            return updatedFeedback;
                        });
                    });
                    if (!updatedFeedback.is_failed) {
                        dispatch({ type: 'SET_SELECTED_FEEDBACK_ID_TO_FIRST', payload: updatedFeedback.id });
                    }
                } catch (e) {
                    console.error(e);
                    message.error('Something went wrong. Please try again.');
                } finally {
                    setIsGenerating(false);
                }
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickThumbsUp = ({ id: targetId }) => {
        const timestamp = new Date().getTime();
        setFeedback((current) => {
            return current.map((feedback) => {
                if (feedback.id !== currentFeedback.id) return feedback;
                const updatedFeedback = {
                    ...currentFeedback,
                    modified_on: timestamp,
                    feedback: {
                        ...currentFeedback.feedback,
                        improvement_suggestions: currentFeedback.feedback.improvement_suggestions.map((item) => {
                            if (item.id !== targetId) return item;
                            return {
                                ...item,
                                agree: !Boolean(item.agree),
                                disagree: false,
                            };
                        }),
                    },
                };
                feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                return updatedFeedback;
            });
        });
    };

    const onClickThumbsDown = ({ id: targetId }) => {
        const timestamp = new Date().getTime();
        setFeedback((current) => {
            return current.map((feedback) => {
                if (feedback.id !== currentFeedback.id) return feedback;
                const updatedFeedback = {
                    ...currentFeedback,
                    modified_on: timestamp,
                    feedback: {
                        ...currentFeedback.feedback,
                        improvement_suggestions: currentFeedback.feedback.improvement_suggestions.map((item) => {
                            if (item.id !== targetId) return item;
                            return {
                                ...item,
                                agree: false,
                                disagree: !Boolean(item.disagree),
                            };
                        }),
                    },
                };
                feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                return updatedFeedback;
            });
        });
    };

    return (
        <PageContext.Provider
            value={{
                dispatch,
                currentFeedback,
                currentFeedbackIndex,
                highlights: getHighlights(),
                selectedFeedbackId,
                setSelectedFeedbackId,
                introductionDialog,
                setIntroductionDialog,
                isGenerating,
                setIsGenerating,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                showDetails,
                setShowDetails,
                onClickBack,
                onClickInfo,
                onClickPrevAttempt,
                onClickNextAttempt,
                onClickRegenerate,
                onClickThumbsUp,
                onClickThumbsDown,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
